import { CmpApi, CmpApiModel } from "@iabtechlabtcf/cmpapi";
import { GVL } from "@iabtechlabtcf/core";
import type { CmpConfigPayload } from "./types";
import { toGvlJson } from "./tcstring";

export type HostedGvl = {
  version: number;
  json: ReturnType<typeof toGvlJson>;
  gvl: GVL;
};

/**
 * Build a GVL instance from our property-trimmed config payload.
 * Never points GVL.baseUrl at IAB — callers must not fetch at runtime.
 */
export function buildHostedGvl(config: CmpConfigPayload): HostedGvl {
  const json = toGvlJson(config);
  return {
    version: config.gvl.vendorListVersion,
    json,
    gvl: new GVL(json as never)
  };
}

/** After CmpApi#update, stamp our GVL onto the model so ping.gvlVersion is set. */
export function attachHostedGvlToCmpApi(hosted: HostedGvl): void {
  const model = CmpApiModel.tcModel;
  if (!model) return;
  model.gvl = hosted.gvl;
  model.vendorListVersion = hosted.version;
}

/**
 * CmpApi#update then attach hosted GVL when gdprApplies (encoded !== null).
 */
export function applyTcUpdate(
  cmpApi: CmpApi,
  hosted: HostedGvl | undefined,
  encoded: string | null,
  uiVisible = false
): void {
  cmpApi.update(encoded, uiVisible);
  if (encoded !== null && hosted) attachHostedGvlToCmpApi(hosted);
}

type TcfApiFn = (
  command: string,
  version: number,
  callback: (result: unknown, success: boolean) => void,
  ...params: unknown[]
) => unknown;

/**
 * getVendorList (optional command, CMP API v2 spec):
 *  - no version → the GVL version encoded in the current TC string, or the latest
 *    if no TC string exists
 *  - 'LATEST' → latest GVL
 *  - integer (or integer string) > 1 → that version
 *  - anything else / unknown version / network error → callback(null, false)
 * Returns the complete GlobalVendorList (not our property-trimmed copy), served
 * from our own origin (`/c/gvl/:version.json`, built from the imported IAB GVL),
 * never fetched from the IAB CDN at runtime. The callback is invoked exactly once.
 */
export function installGetVendorListOverride(getHosted: () => HostedGvl | undefined, apiBase = ""): void {
  const w = window as unknown as { __tcfapi: TcfApiFn };
  const inner = w.__tcfapi.bind(window) as TcfApiFn;

  w.__tcfapi = (command, version, callback, ...params) => {
    if (command === "getVendorList") {
      void serveVendorList(getHosted(), apiBase, callback, params[0]);
      return;
    }
    return inner(command, version, callback, ...params);
  };
}

const vendorListCache = new Map<string, Promise<unknown>>();

export function resolveVendorListVersion(hosted: HostedGvl | undefined, requested?: unknown): string | null {
  if (requested === undefined || requested === null || requested === "") {
    const current = CmpApiModel.tcModel && (CmpApiModel.tcModel as { vendorListVersion?: number }).vendorListVersion;
    if (CmpApiModel.tcString && current && current > 1) return String(current);
    return "latest";
  }
  if (typeof requested === "string" && requested.toUpperCase() === "LATEST") return "latest";
  const n = typeof requested === "number" ? requested : (/^\d+$/.test(String(requested)) ? Number(requested) : NaN);
  if (!Number.isInteger(n) || n <= 1) return null;
  void hosted;
  return String(n);
}

export async function serveVendorList(
  hosted: HostedGvl | undefined,
  apiBase: string,
  callback: (result: unknown, success: boolean) => void,
  requested?: unknown
): Promise<void> {
  if (typeof callback !== "function") return;
  const version = resolveVendorListVersion(hosted, requested);
  if (!version) { callback(null, false); return; }
  try {
    let pending = vendorListCache.get(version);
    if (!pending) {
      pending = fetch(`${apiBase.replace(/\/$/, "")}/c/gvl/${version}.json`, { credentials: "omit" })
        .then((res) => { if (!res.ok) throw new Error(String(res.status)); return res.json(); });
      vendorListCache.set(version, pending);
      pending.catch(() => vendorListCache.delete(version));
    }
    const gvl = await pending;
    callback(gvl, true);
  } catch {
    callback(null, false);
  }
}
